import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';

import RegexValidation from './validator';

/**
 *
 * @typedef {{validate: {Validator: ('Email'|'Url'|'UUID'), pattern: RegExp}, errorMessage: string}} ValidationFeild
 */

/**
 * useForm Hook
 * @template T
 * @typedef {Object.<string,T>} DefaultValues
 * @typedef {keyof DefaultValues} ValuesKey
 * @param {Object} options - Options for UseForm Hook
 * @param {DefaultValues} [options.defaultValues] - Default Field Values for form
 * @param {function(DefaultValues): *} [options.onSubmit] - Default Field Values for form
 * @param {Object<string, ValidationFeild>} [options.validations]
 */
function useForm(options) {
  /**
   * @type [Object.<ValuesKey, string|null>, function(any): any] Errors
   */
  const [errors, setError] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isTouched, setIsTouched] = useState({});
  const isMounted = useRef(false);

  const requiredFields = [];

  Object.keys(options.validations).forEach(key => {
    if (options.validations[key] && options.validations[key].required) {
      requiredFields.push(key);
    }
  });

  // Keep track if current form is mounted or not.
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * @type [DefaultValues, function(any): any] Form Values
   */
  const [values, setValues] = useState(options.defaultValues || {});

  /**
   * Set Error for the Specific Field
   * @param {string} key - Field Name
   * @param {string|null} message - Error Mesaage
   */
  const setErrorField = (key, message) => {
    setError(prevError => ({
      ...prevError,
      [key]: message,
    }));
  };

  /**
   * Set Error for the Specific Field
   * @param {string} key - Field Name
   * @param {string|null} message - Error Mesaage
   */
  const setFieldTouch = key => {
    setIsTouched(prevState => ({
      ...prevState,
      [key]: true,
    }));
  };

  /**
   * Validate current Feild
   * @param {string} key - Field Name
   * @param {any} value - Value of the field
   */
  const valiDateField = (key, value) => {
    let validation;
    if (options.validations && key in options.validations) {
      validation = options.validations[key];

      let validField = true;

      if (validation.pattern) {
        validField = validation.pattern.test(value);
      } else if (
        validation.Validator &&
        validation.Validator in RegexValidation
      ) {
        validField = RegexValidation[validation.Validator].test(value);
      }

      if (value === '' && validation.required) {
        setErrorField(key, validation.requiredMessage);
      } else if (validField) {
        setErrorField(key, null);
      } else if (isTouched[key]) {
        setErrorField(key, validation.errorMessage || '');
      }
    }
    if (!isTouched[key] && validation?.required) {
      setFieldTouch(key);
    }
  };

  /**
   * OnChange Handler
   * @param {string} key - Field Name
   */
  const onChange = key => e => {
    const value = e.target.value;

    valiDateField(key, value);
    setValues(prev => ({ ...prev, [key]: value }));
  };

  /**
   * onBlur Event
   * @param {string} key - Field Key
   */
  const onBlur = key => e => {
    const value = e.target.value;

    valiDateField(key, value);
  };

  /**
   * Reset all the Errors
   */
  const resetErrors = () => {
    const currentErrorState = { ...errors };
    Object.keys(values).forEach(key => {
      currentErrorState[key] = null;
    });

    setError(currentErrorState);
  };

  const fireSubmit = useEventCallback(() => {
    return options.onSubmit(values, { isSubmitting, setIsSubmitting });
  });

  const validateAllFields = useEventCallback(() => {
    Object.keys(values).forEach(key => {
      valiDateField(key, values[key]);
    });
  });

  /**
   * Handle Submition of Form
   */
  const handleSubmit = e => {
    e.preventDefault();

    const hasInvalidField = validateAllFields();

    if (hasInvalidField) {
      return;
    }

    try {
      if (typeof options.onSubmit === 'function') {
        setIsSubmitting(true);
        const promiseOrUndefined = fireSubmit();
        // Bail if it's sync, consumer is responsible for cleaning up
        // via setSubmitting(false)
        if (promiseOrUndefined === undefined) {
          return;
        }

        return Promise.resolve(promiseOrUndefined)
          .then(result => {
            if (!!isMounted.current) {
              setIsSubmitting(false);
            }
            return result;
          })
          .catch(_errors => {
            if (!!isMounted.current) {
              setIsSubmitting(false);
              // This is a legit error rejected by the onSubmit fn
              // so we don't want to break the promise chain
              throw _errors;
            }
          });
      }
    } catch (error) {
      console.error('Error: ', error);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (typeof errors === 'object' && isTouched) {
      const currentErrors = Object.keys(errors).filter(
        er => errors[er] !== null,
      );

      const isAllTouched = Object.keys(isTouched).filter(
        key => isTouched[key] === true,
      );

      if (
        currentErrors.length === 0 &&
        isAllTouched.length === requiredFields.length
      ) {
        setIsValid(true);
      } else {
        setIsValid(false);
      }
    }
  }, [errors, isTouched, requiredFields.length]);

  return {
    onChange,
    onBlur,
    values,
    errors,
    setErrorField,
    resetErrors,
    isSubmitting,
    isValid,
    handleSubmit,
  };
}

// React currently throws a warning when using useLayoutEffect on the server.
// To get around it, we can conditionally useEffect on the server (no-op) and
// useLayoutEffect in the browser.
// @see https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' &&
  typeof window.document !== 'undefined' &&
  typeof window.document.createElement !== 'undefined'
    ? useLayoutEffect
    : useEffect;

function useEventCallback(fn) {
  const ref = useRef(fn);

  // we copy a ref to the callback scoped to the current state/props on each render
  useIsomorphicLayoutEffect(() => {
    ref.current = fn;
  });

  return useCallback((...args) => ref.current.apply(0, args), []);
}

export default useForm;
